import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
	API_URL,
	FIREBASE_AUTH,
	FIRESTORE,
	ORGANIZATION_ID,
} from '@context/frontend/common';
import { isExpired } from '@context/shared/types/common';
import { ExternalSession } from '@context/shared/types/organization';
import {
	User as FirebaseUser,
	onAuthStateChanged,
	signInAnonymously,
	signOut,
} from 'firebase/auth';
import {
	doc,
	DocumentReference,
	DocumentSnapshot,
	getDoc,
	onSnapshot,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

export type ExternalSessionInvalidationReason =
	'expired' | 'deleted' | 'non-existent' | 'logout';

@Injectable({ providedIn: 'root' })
export class ExternalAuthService {
	private readonly auth = inject(FIREBASE_AUTH);
	private readonly firestore = inject(FIRESTORE);
	private readonly http = inject(HttpClient);
	private readonly apiUrl = inject(API_URL);
	private readonly organizationId$ = inject(ORGANIZATION_ID);

	readonly firebaseUser = signal<FirebaseUser | null>(null);
	readonly shareExternalId = signal<string | null>(null);

	private sessionListenerUnsubscribe: (() => void) | null = null;

	constructor() {
		onAuthStateChanged(this.auth, this.onAuthStateChanged.bind(this));
	}

	private onAuthStateChanged(firebaseUser: FirebaseUser | null): void {
		this.firebaseUser.set(firebaseUser);
		if (!firebaseUser) this.reset();
	}

	private reset(): void {
		this.cleanup();
		this.firebaseUser.set(null);
		this.shareExternalId.set(null);
		this.organizationId$.next(null);
	}

	private cleanup(): void {
		if (this.sessionListenerUnsubscribe) {
			this.sessionListenerUnsubscribe();
			this.sessionListenerUnsubscribe = null;
		}
	}

	private async ensureAnonymousUser(): Promise<FirebaseUser> {
		const existingUser = this.auth.currentUser;
		if (existingUser?.isAnonymous) {
			return existingUser;
		}

		if (existingUser) {
			await signOut(this.auth).catch(() => undefined);
		}

		return signInAnonymously(this.auth).then((cred) => cred.user);
	}

	private async redeemExternalShare(externalId: string): Promise<void> {
		await firstValueFrom(
			this.http.post<{ orgId: string; expiresAt: string }>(
				`${this.apiUrl}/v1/users/external-share/authenticate`,
				{ externalId },
			),
		);
		const user = this.auth.currentUser;
		if (!user) {
			throw new Error(
				'Expected anonymous Firebase user after external share redeem',
			);
		}
		await user.getIdToken(true);
		this.shareExternalId.set(externalId);
	}

	/**
	 * Deletes Firestore external session + Firebase Auth user for the current share token (no-op if not applicable).
	 */
	async invalidateViewerSessionOnServer(): Promise<void> {
		try {
			await firstValueFrom(
				this.http.post(
					`${this.apiUrl}/v1/users/external-share/invalidate`,
					{},
				),
			);
		} catch {
			//
		}
	}

	/**
	 * Clears server-side external viewer state when possible, then redeems a fresh token for this link.
	 */
	async resetAndAuthenticateForExternalView(
		orgId: string,
		externalId: string,
	): Promise<boolean> {
		this.organizationId$.next(orgId);
		await this.invalidateViewerSessionOnServer();
		this.cleanup();
		await signOut(this.auth).catch(() => undefined);
		this.firebaseUser.set(null);
		this.shareExternalId.set(null);
		this.organizationId$.next(orgId);
		try {
			const cred = await signInAnonymously(this.auth);
			if (!cred.user) return false;
			this.firebaseUser.set(cred.user);
			await this.redeemExternalShare(externalId);
			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Ensures the current client is authenticated for the external share link.
	 * If existing claims do not match org/external, a new share token is redeemed.
	 */
	async authenticateForShare(
		orgId: string,
		externalId: string,
	): Promise<boolean> {
		const user = await this.ensureAnonymousUser();
		this.organizationId$.next(orgId);
		this.firebaseUser.set(user);

		const tokenResult = await user.getIdTokenResult();
		const claims = tokenResult.claims;
		const hasMatchingShareClaims =
			claims['role'] === 'share' &&
			claims['orgId'] === orgId &&
			claims['shareExternalId'] === externalId;

		if (hasMatchingShareClaims) {
			this.shareExternalId.set(externalId);
			return true;
		}

		try {
			await this.redeemExternalShare(externalId);
		} catch (error: unknown) {
			const status = (error as { status?: number })?.status;
			if (status !== 409) return false;

			await signOut(this.auth);
			this.organizationId$.next(orgId);
			const cred = await signInAnonymously(this.auth);
			if (!cred.user) return false;
			this.firebaseUser.set(cred.user);
			await this.redeemExternalShare(externalId);
		}

		return true;
	}

	async setupExternalSession(
		uid: string,
		orgId: string,
		externalId: string,
	): Promise<boolean> {
		this.organizationId$.next(orgId);
		this.shareExternalId.set(externalId);

		const sessionRef = doc(
			this.firestore,
			'organizations',
			orgId,
			'externalSessions',
			uid,
		) as DocumentReference<ExternalSession>;

		const snapshot = await getDoc(sessionRef);
		if (!snapshot.exists()) return false;

		const data = snapshot.data() as ExternalSession;
		if (!data || data.externalId !== externalId) return false;
		if (data.deletedAt || isExpired(data.expiresAt)) {
			this.cleanup();
			return false;
		}

		this.startSessionListener(sessionRef, externalId);
		return true;
	}

	private startSessionListener(
		sessionRef: DocumentReference<ExternalSession>,
		externalId: string,
	): void {
		this.cleanup();
		this.sessionListenerUnsubscribe = onSnapshot(
			sessionRef,
			(snapshot: DocumentSnapshot<ExternalSession>) => {
				if (!snapshot.exists()) {
					void this.signOut();
					return;
				}

				const data = snapshot.data() as ExternalSession;
				if (!data || data.externalId !== externalId) {
					void this.signOut();
					return;
				}

				if (data.deletedAt || isExpired(data.expiresAt)) {
					void this.signOut();
				}
			},
			(error) => {
				console.error('Error listening to external session:', error);
			},
		);
	}

	readonly signOut = async (
		_reason: ExternalSessionInvalidationReason = 'logout',
	): Promise<void> =>
		signOut(this.auth)
			.catch((error) => {
				console.error(
					'Error signing out external auth session:',
					error,
				);
			})
			.finally(() => this.reset());
}
